import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import {
  isDepositEnabledForOfferType,
  isDepositAvailableOnOffer,
} from 'lib/payment/depositsUtils'
import { rem } from 'polished'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import OfferTilePricing from '../../Support/OfferTilePricing'
import SearchTileFlightOnlyPricing from '../../Support/SearchTileFlightOnlyPricing'
import SoldOutMessage from '../../Support/SoldOutMessage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useDefaultAirport from 'hooks/useDefaultAirport'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import FormatCurrency from 'components/Common/FormatCurrency'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import OfferTileLoggedInButtons from '../../Support/OfferTileLoggedInButtons'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA } from 'constants/offer'
import { isNonEmptyArray } from 'lib/array/arrayUtils'
import config from 'constants/config'
import { calculateDiscount } from 'checkout/selectors/payment/generic'

const PriceDetails = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  margin-top: ${rem(8)};
  position: relative;
  width: auto;
  min-height: ${rem(84)};

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(20)};
  }
  &.landing-page-test {
    margin-top: 0;
  }
  > * + * {
    margin-top: ${rem(8)};
  }
`

const PriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: flex-end;
  flex-direction: column;
  min-width: ${rem(180)};
  align-items: flex-start;
`

const StyledSearchTileFlightOnlyPricing = styled(SearchTileFlightOnlyPricing)`
  align-items: flex-start;
  width: 100%;
`

const AlignedLoader = styled(LoadingIndicator)`
  align-items: flex-start;
`

interface Props {
  offer: App.OfferSummary;
  soldOut: boolean;
  offerUrl: string;
  bestPriceForDates?: App.OfferAvailableRate;
  filters: App.OfferListFilters | undefined;
  checkInDate: moment.Moment | undefined;
  bestPricePackage?: App.Package;
  passedWalledGarden?: boolean;
}

function SearchTilePriceDetails(props: Props) {
  const {
    offer,
    soldOut,
    offerUrl,
    bestPriceForDates,
    filters,
    checkInDate,
    bestPricePackage,
    passedWalledGarden = true,
  } = props
  const { offerFlightsEnabled } = offer

  const { currentRegionHasFlights, currentCurrency, showTaxExclusivePrices } =
    useContext(GeoContext)

  const priceMultiplier = bestPriceForDates ? 1 : filters?.rooms?.length || 1

  const hasDates = !!(filters?.checkIn && filters?.checkOut && isNonEmptyArray(filters?.rooms))

  const defaultAirport = useDefaultAirport()
  const flightPrice = offer.flightPrices[defaultAirport?.code]

  const offerAvailableRates = useOfferAvailableRates(offer?.id, filters)

  const totalPrice = useMemo(() => {
    if (!bestPricePackage) {
      return 0
    }
    return Math.ceil((bestPriceForDates?.price ?? (bestPricePackage.price * (filters?.rooms?.length || 1))) + getSumExtraGuestsSurcharge(bestPricePackage, filters?.rooms ?? [], offer))
  }, [bestPricePackage, filters?.rooms, bestPriceForDates, offer])

  const discountPercentage = useMemo(() => {
    if (bestPricePackage) {
      return calculateDiscount(bestPricePackage.price, bestPricePackage.value)
    }
    return undefined
  }, [bestPricePackage])

  const showSecureWithDepositTag = useMemo(
    () =>
      isDepositEnabledForOfferType(offer.type) &&
      offer.packages.some((pkg) => {
        const propertyFees = pkg?.propertyFees ?
          pkg.propertyFees * priceMultiplier :
          0
        const packagePrice = pkg?.price
        const total = showTaxExclusivePrices ?
          packagePrice :
          packagePrice + propertyFees
        const pkgAvailableRate = offerAvailableRates?.rates?.find(
          (rate) => rate.packageUniqueKey === pkg.uniqueKey,
        )
        return isDepositAvailableOnOffer(
          offer,
          currentCurrency,
          checkInDate,
          total,
          pkg,
          pkgAvailableRate,
        )
      }),
    [
      checkInDate,
      currentCurrency,
      offer,
      offerAvailableRates?.rates,
      priceMultiplier,
      showTaxExclusivePrices,
    ],
  )

  return (
    <PriceDetails>
      <PriceWrapper>
        <Group
          fullWidth
          direction="vertical"
          verticalAlign="start"
          horizontalAlign="start"
          gap={16}
        >
          {bestPricePackage && (!offer.bundledWithFlightsOnly || offer.walledGarden) && (
            <OfferTileLoggedInButtons
              signUpText="Sign up for free"
              discountPercentage={discountPercentage}
              align="left"
              showContent={offer.type === OFFER_TYPE_HOTEL || offer.type === OFFER_TYPE_VILLA}
            >
              <OfferTilePricing
                offer={offer}
                rooms={filters?.rooms}
                hasDates={hasDates}
                checkInDate={checkInDate}
                pkg={bestPricePackage}
                pricing={bestPriceForDates}
                offerUrl={offerUrl}
                align="start"
                flexibleMonths={filters?.flexibleMonths}
                flexibleNights={filters?.flexibleNights}
                hasPackageWithDepositsEnabled={showSecureWithDepositTag}
                filters={filters}
              />
              <TextButton fit="full-width" kind="primary">View offer</TextButton>
            </OfferTileLoggedInButtons>
          )}
          {bestPricePackage && offer.bundledWithFlightsOnly && (
            <StyledSearchTileFlightOnlyPricing
              offer={offer}
              rooms={filters?.rooms}
              hasDates={hasDates}
              pkg={bestPricePackage}
              pricing={bestPriceForDates}
              offerUrl={offerUrl}
            />
          )}
        </Group>
      </PriceWrapper>
      {soldOut && <SoldOutMessage offer={offer} filters={filters} />}
      {currentRegionHasFlights &&
        offerFlightsEnabled &&
        !offer.bundledWithFlightsOnly &&
        config.businessTraveller.currentAccountMode !== 'business' && (
          <CaptionTextBlock variant="medium" startIcon={<LinePlaneFlyIcon colour="primary" />}>
            {!filters?.checkIn && !!flightPrice && passedWalledGarden &&
              <>Hotel + flight package from <FormatCurrency value={flightPrice + totalPrice} /> (2 adults)</>}
            {(filters?.checkIn || !flightPrice || !passedWalledGarden) && 'Hotel + flight package available'}
          </CaptionTextBlock>
      )}
      {!bestPricePackage && <AlignedLoader opaque floating />}
    </PriceDetails>
  )
}

export default SearchTilePriceDetails
